import currency from 'currency.js';
import { SUPPORTED_CURRENCIES } from './constants';
import { TicketInformationResponse } from '@/pages/api/[eventId]/ticket-information.page';
import { isEventCancelled, isEventEnded, isEventPostponed } from '@/components/Body/utils/bodyUtils';
import { IntlShape } from 'react-intl';
import { HighLevelStatus } from '@/utils/highLevelStatus';
import { EVENT_STATUS } from '@/types';

// This value is kept from CORE implementation for keeping the same behavior
const MAX_PRICE_RANGE_LENGTH = 13;

const DEFAULT_CURRENCY_EXPONENT = 2;

const CURRENCY_EXPONENTS: Record<string, number> = {
  JPY: 0,
  KRW: 0,
  VND: 0,
  BHD: 3,
  JOD: 3,
  KWD: 3,
  OMR: 3,
  TND: 3,
};

export const formatV3Price = (
  price: number,
  currencyCode: string,
  locale: string,
  roundUp: boolean = false,
  removeTrailingZeros: boolean = false,
): string => {
  if (!currencyCode || !isValidCurrency(currencyCode)) {
    return '';
  }

  const normalizedCurrency = currencyCode.toUpperCase();
  const exponent = CURRENCY_EXPONENTS[normalizedCurrency] ?? DEFAULT_CURRENCY_EXPONENT;

  let amount = currency(price, { precision: exponent });

  // Convert to major units
  const divisor = Math.pow(10, exponent);
  amount = amount.divide(divisor);

  // Handle round up
  if (roundUp) {
    amount = currency(amount.add(0.99).intValue, { precision: exponent });
  }

  // Use Intl.NumberFormat for locale-specific formatting
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: normalizedCurrency,
    minimumFractionDigits: roundUp ? 0 : exponent,
    maximumFractionDigits: exponent,
  });

  let formatted = formatter.format(amount.value);

  // Remove trailing zeros if requested and not rounding up
  if (removeTrailingZeros && !roundUp) {
    formatted = formatted.replace(/\.0+$/, '');
  }

  return formatted;
};

export const isValidCurrency = (currency: string): boolean => {
  return SUPPORTED_CURRENCIES.includes(currency.toUpperCase());
};

/** Shape used for conversion bar price display (ticket-information and external ticketing). Value in minor units. */
export type PanelPriceInfo = {
  minimumTicketPrice?: { value?: number; currency?: string };
  maximumTicketPrice?: { value?: number; currency?: string };
  isFree?: boolean;
};

/**
 * Formats price for the conversion bar.
 * Supports optional min/max and isFree so it can be used for both ticket-information and external ticketing.
 */
export const getPanelDisplayPriceFromPriceInfo = (
  priceInfo: PanelPriceInfo,
  locale: string,
  formatMessage: IntlShape['formatMessage'],
): string => {
  const minValue = priceInfo.minimumTicketPrice?.value;
  const maxValue = priceInfo.maximumTicketPrice?.value;
  const currency = priceInfo.minimumTicketPrice?.currency ?? priceInfo.maximumTicketPrice?.currency;

  const bothZero = minValue === 0 && maxValue === 0;
  if (priceInfo.isFree || bothZero) {
    return formatMessage({ id: 'Free', defaultMessage: 'Free', description: 'Free event price in conversion bar' });
  }

  const hasMin = minValue !== undefined && minValue != null && currency;
  const hasMax = maxValue !== undefined && maxValue != null && currency;

  if (!hasMin && !hasMax) return '';

  if (hasMin && hasMax) {
    const minPrice = formatV3Price(minValue!, currency!, locale, false, true);
    const maxPrice = formatV3Price(maxValue!, currency!, locale, false, true);
    if (!minPrice || !maxPrice) return '';
    if (minPrice === maxPrice) return minPrice;
    const priceRange = `${minPrice} - ${maxPrice}`;
    return priceRange.length > MAX_PRICE_RANGE_LENGTH
      ? formatMessage({
          id: 'From',
          defaultMessage: 'From',
          description: 'Used for price range in conversion bar for stating only the minimum price',
        }) + ` ${minPrice}`
      : priceRange;
  }

  if (hasMin) {
    return formatV3Price(minValue!, currency!, locale, false, true);
  }
  return formatV3Price(maxValue!, currency!, locale, false, true);
};

export const getPanelDisplayPrice = (
  ticketAvailabilityData: TicketInformationResponse,
  locale: string,
  formatMessage: IntlShape['formatMessage'],
): string => {
  const priceInfo: PanelPriceInfo = {
    minimumTicketPrice: ticketAvailabilityData?.minimumTicketPrice,
    maximumTicketPrice: ticketAvailabilityData?.maximumTicketPrice,
    isFree:
      ticketAvailabilityData?.minimumTicketPrice?.value === 0 &&
      ticketAvailabilityData?.maximumTicketPrice?.value === 0,
  };
  return getPanelDisplayPriceFromPriceInfo(priceInfo, locale, formatMessage);
};

export const shouldShowExploreEventsButton = (status: string): boolean => {
  return isEventEnded(status) || isEventCancelled(status) || isEventPostponed(status);
};

export const shouldShowDiscountSignal = (ticketInfo?: TicketInformationResponse | null): boolean => {
  return !!(
    ticketInfo?.availability?.hasBogoTickets ||
    ticketInfo?.availability?.hasEarlyBirdTickets ||
    (ticketInfo?.discounts && ticketInfo?.discounts?.length > 0)
  );
};

export const shouldDisplayRsvp = (
  mipaParam: string | null,
  status: string,
  highLevelStatus?: HighLevelStatus,
  ticketInfo?: TicketInformationResponse | null,
): boolean => !!mipaParam && shouldShowCheckoutButton(status, highLevelStatus, ticketInfo);

export const shouldShowCheckoutButton = (
  status: string,
  highLevelStatus?: HighLevelStatus,
  ticketInfo?: TicketInformationResponse | null,
  hasReleasedWaitlistClaim: boolean = false,
  isEarlyAccessWindowOpen: boolean = false,
): boolean => {
  if (
    [
      EVENT_STATUS.UNSAVED,
      EVENT_STATUS.DRAFT,
      EVENT_STATUS.CANCELLED,
      EVENT_STATUS.DELETED,
      EVENT_STATUS.POSTPONED,
      EVENT_STATUS.COMPLETED,
      EVENT_STATUS.ENDED,
    ].includes(status)
  ) {
    return false;
  }

  // An open Early Access window lifts only the not-yet-on-sale suppression; every other gate below
  // still applies (sold out, unavailable, cancelled, etc.).
  const blockedHighLevelStatuses = [
    HighLevelStatus.SALES_ENDED,
    HighLevelStatus.UNAVAILABLE,
    HighLevelStatus.POSTPONED,
    HighLevelStatus.CANCELLED,
    HighLevelStatus.EVENT_ENDED,
    HighLevelStatus.COMPLETED,
    ...(isEarlyAccessWindowOpen ? [] : [HighLevelStatus.SALES_START_SOON]),
  ];

  if (highLevelStatus && blockedHighLevelStatuses.includes(highLevelStatus)) {
    return false;
  }

  if (
    ticketInfo?.isSoldOut === true &&
    !ticketInfo?.availability?.hasAvailableHiddenTickets &&
    !hasReleasedWaitlistClaim &&
    !ticketInfo?.availability?.waitlistAvailable &&
    !ticketInfo?.availability?.waitlistEnabled
  ) {
    return false;
  }

  if (highLevelStatus === HighLevelStatus.SOLD_OUT && ticketInfo?.isSoldOut === undefined) {
    return false;
  }

  return true;
};

export const shouldShowSoldOutText = (
  highLevelStatus: HighLevelStatus,
  ticketInfo?: TicketInformationResponse | null,
): boolean => {
  return highLevelStatus === HighLevelStatus.SOLD_OUT || ticketInfo?.isSoldOut === true;
};
